import {
    TemplateQuestionResponseType,
    TemplateResponseType,
    isDisplayOnlyInspectionQuestion,
} from './constant/template'

/** Answer payload marker. Parents apply one shared radio answer to the whole section. */
export const UNIFORM_RADIO_ANSWER_FIELD = '__uniformRadioDisplayText'

const answerText = option => String(option?.DisplayText ?? '').trim()

const isImportedSection = section =>
    !!(section?.IsImported || section?.SectionName === 'Imported section')

const hasParentChildAnswers = question => {
    const parentChild = question?.Answers?.ParentChildResponse
    if (Array.isArray(parentChild)) return parentChild.length > 0
    return !!parentChild
}

const hasFilterAnswers = question =>
    Array.isArray(question?.Answers?.FilterAnswers) &&
    question.Answers.FilterAnswers.length > 0

/**
 * Same answer labels, same order-independent set, and the same Other flag.
 * Returns null when the options are not a safe single-select radio set.
 */
const radioAnswerSignature = question => {
    const options = question?.Answers?.MultipleResponse
    if (!Array.isArray(options) || !options.length) return null
    const texts = options.map(answerText)
    if (texts.some(text => !text)) return null
    if (new Set(texts).size !== texts.length) return null
    const otherFlag = question?.IsOtherOptionAvailable ? '1' : '0'
    return `${otherFlag}|${[...texts].sort().join('\u0001')}`
}

const isUniformRadioQuestion = question => {
    if (!question || question.Id == null || question.Id === '') return false
    if (question.IsReadOnly || question.IsImported) return false
    const responseType = Number(question.TypeOfResponse ?? question.QuestionType)
    if (responseType !== TemplateResponseType.Multiple) return false
    // Same check the radio UI uses. String "1" renders as a dropdown, not radios.
    if (question.MultiResponseType !== TemplateQuestionResponseType.Single) return false
    if (hasParentChildAnswers(question) || hasFilterAnswers(question)) return false
    return radioAnswerSignature(question) != null
}

/**
 * Section can use one click to set the same radio answer on every question
 * only when every answerable question is a single-select radio and every
 * question exposes the same answer labels (for example Yes and No).
 * Display-only header, label, and auto-number rows are ignored.
 * Returns null for every other section so existing answers stay untouched.
 */
export const getSectionUniformRadio = section => {
    if (!section || isImportedSection(section)) return null
    const questions = Array.isArray(section.Questions) ? section.Questions : []
    const answerable = []

    for (let index = 0; index < questions.length; index += 1) {
        const question = questions[index]
        if (question && !isDisplayOnlyInspectionQuestion(question)) {
            if (!isUniformRadioQuestion(question)) return null
            answerable.push(question)
        }
    }

    if (answerable.length < 2) return null
    const signature = radioAnswerSignature(answerable[0])
    if (!signature) return null

    for (let index = 1; index < answerable.length; index += 1) {
        if (radioAnswerSignature(answerable[index]) !== signature) return null
    }

    return {
        questions: answerable,
        questionIds: new Set(answerable.map(question => String(question.Id))),
        optionTexts: (answerable[0].Answers?.MultipleResponse || []).map(answerText),
    }
}

/** The shared selected label, or null when answers are missing or mixed. */
export const getUniformSelectedDisplayText = questions => {
    if (!Array.isArray(questions) || !questions.length) return null
    let selected = null

    for (let index = 0; index < questions.length; index += 1) {
        const question = questions[index]
        if (
            question?.IsOtherAnswerSelected ||
            question?.Answers?.IsOtherAnswerSelected
        ) {
            return null
        }
        const selectedOptions = (question?.Answers?.MultipleResponse || []).filter(
            option => option?.IsSelected,
        )
        if (selectedOptions.length !== 1) return null
        const text = answerText(selectedOptions[0])
        if (!text) return null
        if (index === 0) selected = text
        else if (selected !== text) return null
    }

    return selected
}

export const isUniformRadioAnswer = answer =>
    !!answer &&
    typeof answer === 'object' &&
    Object.prototype.hasOwnProperty.call(answer, UNIFORM_RADIO_ANSWER_FIELD)

export const readUniformRadioDisplayText = answer => {
    if (!isUniformRadioAnswer(answer)) return null
    const text = String(answer[UNIFORM_RADIO_ANSWER_FIELD] ?? '').trim()
    return text || null
}

/** Individual radio edits clear IsAllAllow. Notes, media, and CNC updates do not. */
export const shouldClearSectionAllAllow = answer => {
    if (readUniformRadioDisplayText(answer)) return false
    if (answer && typeof answer === 'object') {
        if (Object.prototype.hasOwnProperty.call(answer, '__ncReply')) return false
        if (Object.prototype.hasOwnProperty.call(answer, 'MultipleResponse')) return true
        if (Object.prototype.hasOwnProperty.call(answer, 'ParentChildResponse')) {
            return true
        }
        if (
            Object.prototype.hasOwnProperty.call(answer, 'Response') &&
            Object.prototype.hasOwnProperty.call(answer, 'IsSelected')
        ) {
            return true
        }
        return false
    }
    return answer !== undefined
}

const templateOptionMatches = (templateOption, option, question) => {
    if (!templateOption || templateOption.DisplayText !== option.DisplayText) {
        return false
    }
    if (templateOption.Id === option.Id) return true
    if (templateOption.GroupId === option.QuestionResponseId) return true
    if (templateOption.Id === option.QuestionResponseId) return true
    if (
        question?.QuestionResponseId &&
        (templateOption.GroupId === question.QuestionResponseId ||
            templateOption.Id === question.QuestionResponseId)
    ) {
        return true
    }
    return false
}

const resolveMultipleOptionScore = (option, question, templateMultipleResponse) => {
    if (typeof option?.Score === 'number') return option.Score
    if (!Array.isArray(templateMultipleResponse)) return option?.Score
    const templateMatch = templateMultipleResponse.find(templateOption =>
        templateOptionMatches(templateOption, option, question),
    )
    if (templateMatch && typeof templateMatch.Score === 'number') {
        return templateMatch.Score
    }
    return option?.Score
}

const applyUniformRadioToQuestion = (
    question,
    displayText,
    templateMultipleResponse,
) => {
    const multipleResponse = (question?.Answers?.MultipleResponse || []).map(
        option => {
            const score = resolveMultipleOptionScore(
                option,
                question,
                templateMultipleResponse,
            )
            return {
                ...option,
                IsSelected: answerText(option) === displayText,
                ...(typeof score === 'number' ? { Score: score } : {}),
            }
        },
    )
    const selected = multipleResponse.filter(option => option.IsSelected)
    if (selected.length !== 1) return null
    const score = typeof selected[0].Score === 'number' ? selected[0].Score : 0

    return {
        ...question,
        IsAllAllow: true,
        IsOtherAnswerSelected: false,
        IsAnswered: true,
        Score: score,
        Answers: {
            ...(question.Answers || {}),
            MultipleResponse: multipleResponse,
            IsOtherAnswerSelected: false,
            OtherAnswerResponse: null,
            IsAllAllow: true,
            IsAnswered: true,
        },
    }
}

/**
 * Selects the same radio label on every qualifying question and sets IsAllAllow.
 * Returns null when the section or label is not eligible, leaving data unchanged.
 */
export const applyUniformRadioToSection = (
    section,
    displayText,
    templateMultipleResponse,
) => {
    const uniform = getSectionUniformRadio(section)
    const target = String(displayText ?? '').trim()
    if (!uniform || !target || !uniform.optionTexts.includes(target)) return null

    const nextQuestions = []
    const questions = section.Questions || []
    for (let index = 0; index < questions.length; index += 1) {
        const question = questions[index]
        if (!uniform.questionIds.has(String(question?.Id))) {
            nextQuestions.push(question)
        } else {
            const nextQuestion = applyUniformRadioToQuestion(
                question,
                target,
                templateMultipleResponse,
            )
            if (!nextQuestion) return null
            nextQuestions.push(nextQuestion)
        }
    }

    return {
        ...section,
        IsAllAllow: true,
        Questions: nextQuestions,
    }
}

/** Turns IsAllAllow off after a later per-question answer edit. No-op when unset. */
export const clearSectionAllAllow = section => {
    if (!section) return section
    const questions = Array.isArray(section.Questions) ? section.Questions : []
    const hasFlag =
        section.IsAllAllow === true ||
        questions.some(
            question =>
                question?.IsAllAllow === true ||
                question?.Answers?.IsAllAllow === true,
        )
    if (!hasFlag) return section

    return {
        ...section,
        IsAllAllow: false,
        Questions: questions.map(question => {
            if (
                question?.IsAllAllow !== true &&
                question?.Answers?.IsAllAllow !== true
            ) {
                return question
            }
            return {
                ...question,
                IsAllAllow: false,
                Answers: question.Answers
                    ? { ...question.Answers, IsAllAllow: false }
                    : question.Answers,
            }
        }),
    }
}
